import {v5 as uuidv5} from 'uuid';

import ManagerRepository from 'brandClasses/ManagerRepository';
import Manager from 'coreClasses/Manager';
import {
	receiveProducts,
	selectProduct,
	cleanupProducts,
	markToBeRefreshed,
	selectProductVariation,
	markReady
} from 'coreStore/data/products';
import {Product} from 'coreStore/data/products/types';
import ProductModel from './ProductModel';
import VariantModel from './VariantModel';
import {isServer, replaceDISCacheVersion, timeout} from 'coreUtils/utils';
import ProductFactory from './ProductFactory';
import {EErrorType} from '../errorsStream/IErrorMessage';
import WorkerMessenger from 'coreClasses/worker/WorkerMessenger';
import {CustomerGroup} from 'coreStore/data/customer/types';
import {selectBasket} from 'coreStore/data/basket';
import AbstractProductModel from './AbstractProductModel';
import PWAException from 'coreClasses/PWAException';
import {ICalculateCustomProductPriceResponse} from 'commerceAPI/custom-apis/CustomShopperProduct/ICustomShopperProduct';

const MAX_PRODUCTS = 24;

interface ProductsPromises {
	[key: string]: Promise<unknown>;
}

interface ProductsFetchOptions {
	skipVariantFetch?: boolean;
}

/**
 * Represents ProductsMgr
 *
 * Products are fetched on SSR but refetched again on CSR because prices depend on the current user
 * and we don't have this information on SSR; So now we are fetching all products twice.
 * We need the products on SSR because of SEO and we need them on CSR because of prices.
 */
class ProductsMgr extends Manager {
	private loadingPids: Array<string>;
	private loadingPlaceHolders: string[] = [];
	private loadingPidsPromises: ProductsPromises;
	private factory: ProductFactory;
	private pidsToReceive = [];
	private fetchingTimeout;
	private pidsToSkipVariants = new Set<string>();
	private pidsRequiringVariants = new Set<string>();

	/**
	 * @constructor
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 */
	constructor(managerRepository: ManagerRepository) {
		super(managerRepository);

		this.factory = new ProductFactory(this.managerRepository);
		this.loadingPids = []; // Array of product ids that are currently being fetched
		this.loadingPidsPromises = {};
	}

	/**
	 * Creates new product model
	 * @param {Product} storedProduct - product data in store
	 * @returns {AbstractProductModel}
	 */
	create(storedProduct: Product): AbstractProductModel {
		return this.factory.create(storedProduct);
	}

	/**
	 * Gets multiple products from store or API using getProducts api call
	 * @param {Array<String>} pids Array of product ids
	 * @param {Boolean} forceFetch Executes API call even if products are already stored
	 * @param {ProductsFetchOptions} [options] Optional fetch options
	 * @param {boolean} [options.skipVariantFetch] When true, skip fetching individual variants for VGs (marks them ready immediately)
	 * @returns {Array<ProductModel>} Array of products
	 */
	getProducts(pids: Array<string>, forceFetch: boolean = false, options?: ProductsFetchOptions) {
		if (!pids.length) {
			return pids;
		}

		if (options?.skipVariantFetch === true) {
			pids.forEach(pid => {
				if (!this.pidsRequiringVariants.has(pid)) {
					this.pidsToSkipVariants.add(pid);
				}
			});
		} else if (options?.skipVariantFetch === false) {
			pids.forEach(pid => {
				this.pidsToSkipVariants.delete(pid);
				this.pidsRequiringVariants.add(pid);
			});
		}

		const pidsToReceive = pids.filter((pid) => {
			return this.isProductShouldBeFetched(pid, forceFetch);
		});
		const maxProductsCount = this.getMaxProductsCount();

		this.loadingPids = [...new Set([...this.loadingPids, ...pidsToReceive])];
		this.pidsToReceive = [
			...new Set([...this.pidsToReceive, ...pidsToReceive])
		];

		const pidsToNotReceive = pids.filter((pid) =>
			!pidsToReceive.includes(pid) && !this.loadingPids.includes(pid)
		);

		clearTimeout(this.fetchingTimeout);
		this.fetchingTimeout = setTimeout(() => {
			const localPids = [...this.pidsToReceive];

			this.pidsToReceive = [];

			if (localPids?.length) {
				for (let i = 0; i < localPids.length; i += maxProductsCount) {
					this.fetchProducts(localPids.slice(i, i + maxProductsCount));
				}
			}
		}, 10);

		if (pidsToNotReceive?.length) {
			this.storePlaceholderProducts(pidsToNotReceive);

			if (!isServer) {
				const vgWithMissingVariants = this.getStoredVariationGroupsWithMissingVariants(pidsToNotReceive);

				if (vgWithMissingVariants.length) {
					const vgsToFetch = vgWithMissingVariants.filter(
						p => !this.pidsToSkipVariants.has(p.id) || this.pidsRequiringVariants.has(p.id)
					);
					const vgsToSkip = vgWithMissingVariants.filter(
						p => this.pidsToSkipVariants.has(p.id) && !this.pidsRequiringVariants.has(p.id)
					);

					if (vgsToFetch.length > 0) {
						this.fetchVariantsFromProducts(vgsToFetch);
					}

					if (vgsToSkip.length > 0) {
						this.managerRepository.getStoreMgr().getStore().dispatch(
							markReady(vgsToSkip.map(p => p.id))
						);
					}
				}
			}
		}

		return pids.map((pid) => this.create(this.getStoredProduct(pid)));
	}

	/**
	 * Gets product from store or API using getProduct api call
	 * @param {String} pid Product ID
	 * @param {Boolean} forceFetch Executes API call even if product is already stored
	 * @returns {ProductModel} Wrapped product from store
	 */
	getProduct(pid: string, forceFetch: boolean = false) {
		const products = this.getProducts([pid], forceFetch);

		return products.length > 0 ? products[0] : null;
	}

	/**
	 * Gets product from store or API using getProduct api call in sync mode
	 * @param {String} pid Product ID
	 * @param {Boolean} forceFetch Executes API call even if product is already stored
	 * @returns {ProductModel} Wrapped product from store
	 */
	async getProductSync(pid: string, forceFetch: boolean = false): Promise<ProductModel | VariantModel> {
		while (!this.managerRepository.getCustomerMgr().isContextAvailable()) {
			await timeout(250);
		}

		if (pid in this.loadingPidsPromises && !forceFetch) {
			await this.loadingPidsPromises[pid];
		} else if (this.isProductRequiredBySyncCall(pid, forceFetch)) {
			await this.fetchProduct(pid);
		} else {
			this.storePlaceholderProducts([pid]);
		}

		const storedProduct = this.getStoredProduct(pid, true);

		return storedProduct && this.create(storedProduct) as ProductModel | VariantModel;
	}

	/**
	 * Gets product from store or API using getProduct api call in sync mode
	 * @param {Array<string>} pids Array of product IDs
	 * @param {Boolean} forceFetch Executes API call even if product is already stored
	 * @param {ProductsFetchOptions} [options] Optional fetch options
	 * @param {boolean} [options.skipVariantFetch] When true, skip fetching individual variants for VGs (marks them ready immediately)
	 * @returns {ProductModel} Wrapped product from store
	 */
	async getProductsSync(pids: Array<string>, forceFetch: boolean = false, options?: ProductsFetchOptions) {
		while (!this.managerRepository.getCustomerMgr().isContextAvailable()) {
			await timeout(250);
		}

		if (options?.skipVariantFetch === true) {
			pids.forEach(pid => {
				if (!this.pidsRequiringVariants.has(pid)) {
					this.pidsToSkipVariants.add(pid);
				}
			});
		} else if (options?.skipVariantFetch === false) {
			pids.forEach(pid => {
				this.pidsToSkipVariants.delete(pid);
				this.pidsRequiringVariants.add(pid);
			});
		}

		const pidsToReceive = pids.filter((pid) => this.isProductRequiredBySyncCall(pid, forceFetch));
		const pidsToNotReceive = pids.filter((pid) => !pidsToReceive.includes(pid));

		if (pidsToReceive?.length) {
			const fetchProductsPromises = [];
			const maxProductsCount = this.getMaxProductsCount();

			for (let i = 0; i < pidsToReceive.length; i += maxProductsCount) {
				fetchProductsPromises.push(this.fetchProducts(pidsToReceive.slice(i, i + maxProductsCount)));
			}

			await Promise.all(fetchProductsPromises);
		}

		if (pidsToNotReceive?.length) {
			this.storePlaceholderProducts(pidsToNotReceive);

			if (!isServer) {
				const vgWithMissingVariants = this.getStoredVariationGroupsWithMissingVariants(pidsToNotReceive);

				if (vgWithMissingVariants.length) {
					const vgsToFetch = vgWithMissingVariants.filter(
						p => !this.pidsToSkipVariants.has(p.id) || this.pidsRequiringVariants.has(p.id)
					);

					if (vgsToFetch.length > 0) {
						await this.fetchVariantsFromProducts(vgsToFetch);
					}
				}
			}
		}

		for (let i = 0; i < pids?.length; ++i) {
			if (this.loadingPidsPromises[pids[i]]) {
				await this.loadingPidsPromises[pids[i]];
			}
		}

		return pids.map((pid) => this.create(this.getStoredProduct(pid, true)));
	}

	/**
	 * Removes all stored products from redux store
	 */
	cleanupProducts() {
		this.managerRepository.getStoreMgr().getStore().dispatch(cleanupProducts());
	}

	/**
	 * Marks products to be refreshed
	 */
	markProductsToBeRefreshed() {
		const awaitLoadingProducts = () => {
			const loadingPromisesArray = Object.values(this.loadingPidsPromises) || [];

			if (loadingPromisesArray.length) {
				return Promise.all(loadingPromisesArray).then(awaitLoadingProducts);
			} else {
				return Promise.resolve();
			}
		};

		awaitLoadingProducts().then(() => {
			this.pidsToSkipVariants.clear();
			this.pidsRequiringVariants.clear();
			this.doMarkProductsToBeRefreshed();
			this.managerRepository.getPromotionsMgr().cleanupPromotions();
		});
	}

	/**
	 * Stores placeholder products in the state for not fetched products
	 * This enables us to receive redux update states when real products
	 * will be fetched.
	 * @param {String[]} pids - product IDs
	 * @private
	 */
	private storePlaceholderProducts(pids: string[]) {
		const filteredPids = pids
			.filter((pid) => !this.getStoredProduct(pid, true))
			.filter((pid) => !this.loadingPlaceHolders.includes(pid));
		this.loadingPlaceHolders = [...this.loadingPlaceHolders, ...filteredPids];

		if (filteredPids.length) {
			const placeholders = filteredPids.map((pid) => ({id: pid, type: {placeholder: true}}));

			this.storeProducts(placeholders);
			this.loadingPlaceHolders = this.loadingPlaceHolders.filter((pid) => !filteredPids.includes(pid));
		}
	}

	/**
	 * Executes API call to get multiple products
	 * @param {Array<string>} pids Array of product ids
	 */
	private async fetchProducts(pids: Array<string>) {
		this.loadingPids = this.loadingPids.concat(pids);

		let skippedVgIds: string[] = [];

		const operationResult = this.managerRepository.getCommerceApi().shopperProducts.getProducts({
			parameters: {
				ids: pids.join(','),
				allImages: true,
				expand: 'recommendations,availability,links,promotions,options,images,prices,variations,set_products' as any,
				perPricebook: true,
				inventoryIds: this.getAdditionalCacheKeys().join('|')
			},
		});

		pids.forEach((pid) => {
			this.loadingPidsPromises[pid] = operationResult;
		});

		const products = await operationResult;
		const offlineProductsIds = pids.filter((pid) => !products.data?.some(p => p.id === pid));

		if (products.data && !isServer) {
			// Notify the existence of at least one product without prices.
			const productNoPrice = products.data.filter((p) => !p.price);

			if (productNoPrice.length) {
				const basket = selectBasket(this.managerRepository.getStoreMgr().getStore().getState());
				const currentLocale = this.managerRepository.getLocalesMgr().getCurrentLocale();
				const productsIds = productNoPrice.map(p => p.id);
				this.managerRepository.getErrorsStreamMgr().notify(EErrorType.PRODUCT_NO_PRICE, {
					pageURL: window.location.href,
					products: {
						ids: productsIds,
						currency: productNoPrice[0].currency,
					},
					currentSite: currentLocale.getCurrentSiteId(),
					countryCode: currentLocale.getCountryCode(),
					languageCode: currentLocale.getLanguageCode(),
					currencyCode: currentLocale.getCurrencyCode(),
					authType: this.managerRepository.getCustomerMgr().getCurrentCustomer().authType,
					basketCurrency: basket.currency
				});
			}

			const allVgs = products.data.filter((p) => p.type?.variationGroup);
			const vgsToFetch = allVgs.filter(p => !this.pidsToSkipVariants.has(p.id));
			const vgsToSkip = allVgs.filter(p => this.pidsToSkipVariants.has(p.id));

			if (vgsToFetch.length > 0) {
				this.fetchVariantsFromProducts(vgsToFetch);
			}

			skippedVgIds = vgsToSkip.map((p) => p.id);
		}

		this.loadingPids = this.loadingPids.filter((el) => !pids.includes(el));
		pids.forEach((pid) => {
			delete this.loadingPidsPromises[pid];
		});
		this.storeProducts(products.data);

		if (skippedVgIds.length > 0) {
			this.managerRepository.getStoreMgr().getStore().dispatch(
				markReady(skippedVgIds)
			);
		}

		if (offlineProductsIds.length) {
			const offlineProducts = offlineProductsIds.map((id) => ({
				id,
				offline: true,
				type: {}
			}));
			this.storeProducts(offlineProducts);
		}
	}

	/**
	 * Checks if product should be fetched
	 * If the product is fetched on SSR, it should be fetched again on CSR to get actual prices
	 * @param {String} pid Product ID
	 * @param {Boolean} forceFetch Executes API call even if products are already stored
	 * @returns {Boolean} Returns true if product should be fetched
	 */
	private isProductShouldBeFetched(pid: string, forceFetch: boolean = false) {
		// Do not fetch prices until we get customer & customer context
		if (
			!this.managerRepository.getCustomerMgr().isContextAvailable()
			|| !this.managerRepository.getCustomerMgr().getCurrentCustomer().isInitialized
		) {
			return false;
		}

		if (!pid) {
			return false;
		}

		if (forceFetch) {
			return true;
		}

		if (pid && this.loadingPids.includes(pid)) {
			return false;
		}

		const storedProduct = this.getStoredProduct(pid);

		if (!storedProduct || !storedProduct.id) {
			return true;
		}

		if (storedProduct.isFetchedOnSSR && !isServer) {
			return true;
		}

		if (storedProduct && storedProduct.toBeRefreshed) {
			return true;
		}

		return false;
	}

	/**
	 * Whether `getProductSync`/`getProductsSync` must fetch the product: as `isProductShouldBeFetched`, and also when
	 * the product is neither stored nor being fetched. That happens before the customer is initialized (client
	 * navigation right after hydration: the API call waits for the customer) or while the pid waits in a
	 * `getProducts` batch not sent yet. The callers decide on the result (e.g. the PDP renders a 404 for a
	 * product without id): a placeholder is never a valid answer for them.
	 * @param {String} pid Product ID
	 * @param {Boolean} forceFetch Executes API call even if products are already stored
	 * @returns {Boolean} Returns true if product must be fetched
	 */
	private isProductRequiredBySyncCall(pid: string, forceFetch: boolean = false) {
		if (!pid) {
			return false;
		}

		return this.isProductShouldBeFetched(pid, forceFetch)
			|| (!(pid in this.loadingPidsPromises) && !this.getStoredProduct(pid)?.id);
	}

	/**
	 * Executes API call to get full information about one product
	 * @param {String} pid Product ID
	 */
	private async fetchProduct(pid: string) {
		await this.fetchProducts([pid]);
	}

	/**
	 * Fetches all variants products from the given variationGroups products array
	 * @param products {Array}
	 * @returns {Promise<[Product]>} a promise resolving product variants
	 * @private
	 */
	private async fetchVariantsFromProducts(products): Promise<void> {
		const variantsIds = products.reduce((array, p) => {

			if (p.type.variationGroup && p.variants?.length) {
				return [...array, ...p.variants.map((v) => v.productId)];
			}

			return array;
		}, [])
			.filter((v) => {
				if (this.isProductShouldBeFetched(v)) {
					this.loadingPids.push(v);

					return true;
				}

				return false;
			}).sort();
		const maxProductsCount = this.getMaxProductsCount();

		const variantsIdsChunks = Array.from(
			{length: Math.ceil(variantsIds.length / maxProductsCount)},
			(v, i) => variantsIds.slice(i * maxProductsCount, i * maxProductsCount + maxProductsCount)
		);

		return Promise.all(
			variantsIdsChunks?.map((chunk) => {
				return this.managerRepository.getCommerceApi().shopperProducts
					.getProducts({
						parameters: {
							ids: chunk.join(','),
							perPricebook: true,
							inventoryIds: this.getAdditionalCacheKeys().join('|')
						},
					})
					.then((variants) => {
						this.loadingPids = this.loadingPids.filter((el) => !chunk.includes(el));

						return variants;
					});
			}) || []
		).then((chunk) => {
			chunk.forEach((variants) => this.storeProducts(variants.data));

			const store = this.managerRepository.getStoreMgr().getStore();

			store.dispatch(markReady(products.map((p) => p.id)));
		});
	}

	/**
	 * Returns maximum number of products that can be fetched in one API call
	 * @returns {number} Returns maximum number of products that can be fetched in one API call
	 */
	private getMaxProductsCount(): number {
		return MAX_PRODUCTS;
	}

	/**
	 * Gets product that are already fetched and stored in redux
	 * @returns Products
	 */
	private getStoredProduct(pid: string, includePlaceholders = false) {
		const store = this.managerRepository.getStoreMgr().getStore();
		let product = selectProduct(store.getState(), pid);

		if (!product) {
			product = selectProductVariation(store.getState(), pid);
		}

		if (product?.type?.placeholder && !includePlaceholders) {
			return null;
		}

		return product;
	}

	/**
	 * Returns stored variation group products whose variants are not yet fetched
	 * @param {string[]} pids - product IDs already in the store
	 * @returns {Product[]} variation group products with missing variants
	 * @private
	 */
	private getStoredVariationGroupsWithMissingVariants(pids: string[]): Product[] {
		return pids
			.map(pid => this.getStoredProduct(pid, true))
			.filter(product =>
				product?.type?.variationGroup
				&& product?.variants?.length
				&& product.variants.some(v => this.isProductShouldBeFetched(v.productId))
			);
	}

	/**
	 * Adds products to redux store
	 * @param productData {Array} Array of products
	 */
	private storeProducts(productData) {
		const DIS_STATIC_PATH = this.managerRepository.getEnvPreferencesMgr().get('DIS_STATIC_PATH');
		const store = this.managerRepository.getStoreMgr().getStore();
		let products = {};

		if (DIS_STATIC_PATH && productData) {
			replaceDISCacheVersion(productData, DIS_STATIC_PATH);
		}

		productData?.forEach((product) => {
			products[product.id] = {
				...product,
				isFetchedOnSSR: isServer,
			};
		});

		store.dispatch(receiveProducts(products));
	}

	/**
	 * Marks products to be refreshed
	 */
	private doMarkProductsToBeRefreshed() {
		const store = this.managerRepository.getStoreMgr().getStore();

		store.dispatch(markToBeRefreshed());

		if (!isServer) {
			WorkerMessenger.clearContextCaches();
		}
	}

	/**
	 * Returns additional cache key to be used in API call
	 * @returns {string[]} Returns additional cache key to be used in API call
	 */
	private getAdditionalCacheKeys(): string[] {
		const basket = selectBasket(this.managerRepository.getStoreMgr().getStore().getState());
		const keys = this.managerRepository
			.getCustomerMgr()
			.getCurrentCustomer()
			.getCustomerGroups()
			?.map((customerGroup: CustomerGroup) => customerGroup.ID) || [];

		if (basket.couponItems && Array.isArray(basket.couponItems)) {
			basket.couponItems.forEach((c) => {
				if (c.valid && c.statusCode === 'applied') {
					keys.push(c.code);
				}
			});
		}

		return [uuidv5(keys.join(','), uuidv5.URL)];
	}

	/**
	 * Calculates a custom price for a specific product using a commerce API.
	 *
	 * This method sends a request to the API with the product ID and additional
	 * configuration details in the request body to calculate the custom price.
	 * If the API responds with an error, a `PWAException` is thrown.
	 *
	 * @async
	 * @param {string} productId - The unique identifier of the product for which the custom price is to be calculated.
	 * @param {object} body - The request body containing additional parameters required for price calculation.
	 * @returns {Promise<ProductModel>} A promise that resolves to a `ProductModel` object containing the calculated price and product details.
	 * @throws {PWAException} Throws an exception if the API responds with an error.
	 */
	async calculateCustomProductPrice(productId: string, body: object): Promise<ICalculateCustomProductPriceResponse> {
		const response = await this.managerRepository
			.getCommerceApi()
			.customShopperProduct
			.calculateCustomProductPrice({
				pathParameters: {
					c_productId: productId,
				},
				body
			});

		if ((response as any).error) {
			throw new PWAException(null, (response as any).errorMessage);
		}

		return response;
	}
}

export default ProductsMgr;
